import { useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Testimonial } from '@common';
import { PrimaryButton } from '@components/atoms';
import { Col, Form, Input, Modal, Row, Upload, Rate } from 'antd';
import { useEffect } from 'react';
import { UploadFile } from 'antd/es/upload/interface';
import './styles.css';

const { TextArea } = Input;

interface AddTestimonialModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (testimonial: any) => void;
  initialValues?: Testimonial;
}

export const AddTestimonialModal = ({ isOpen, onCancel, onSave, initialValues }: AddTestimonialModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setAvatarFile(null);

      if (initialValues) {
        const formValues = {
          ...initialValues,
          stars: initialValues.stars || 5,
        };
        form.setFieldsValue(formValues);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleAvatarUpload = (file: UploadFile) => {
    setAvatarFile(file as unknown as File);
    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const testimonialData = {
        name: values.name,
        description: values.description,
        text: values.text,
        stars: values.stars || 5,
        isActive: true, // Always set to true for new testimonials
        avatar: avatarFile,
      };

      onSave(testimonialData);
      form.resetFields();
      setAvatarFile(null);

    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = initialValues
    ? <div className="!font-['Android_101'] !font-normal">EDIT TESTIMONIAL</div>
    : <div className="!font-['Android_101'] !font-normal">ADD NEW TESTIMONIAL</div>;

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="testimonial-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={<span className="text-white font-['Barlow']">CLIENT NAME</span>}
              rules={[
                { required: true, message: 'Please enter the client name' },
                { min: 2, message: 'Name must be at least 2 characters' },
                { max: 100, message: 'Name must not exceed 100 characters' }
              ]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: David Thompson" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={<span className="text-white font-['Barlow']">JOB TITLE / COMPANY</span>}
              rules={[
                { max: 200, message: 'Description must not exceed 200 characters' }
              ]}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: Thompson Development Group" 
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="text"
              label={<span className="text-white font-['Barlow']">TESTIMONIAL TEXT</span>}
              rules={[
                { required: true, message: 'Please enter the testimonial text' },
                { min: 10, message: 'Testimonial must be at least 10 characters' },
                { max: 1000, message: 'Testimonial must not exceed 1000 characters' }
              ]}
            >
              <TextArea
                rows={4}
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Enter the client's testimonial..."
                showCount
                maxLength={1000}
                style={{ backgroundColor: '#27272A' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="stars"
              label={<span className="text-white font-['Barlow']">RATING</span>}
              initialValue={5}
            >
              <Rate 
                className="!text-[#E3AF25]"
                allowHalf={false}
                count={5}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="avatar"
              label={<span className="text-white font-['Barlow']">CLIENT AVATAR</span>}
            >
              <Upload
                name="avatar"
                listType="text"
                accept="image/*"
                beforeUpload={handleAvatarUpload}
                maxCount={1}
                showUploadList={{
                  showRemoveIcon: true,
                }}
              >
                <PrimaryButton 
                  className="!border-gray-700 !text-white !font-['Barlow'] !bg-[#27272A] !rounded-none"
                  icon={<UploadOutlined className="!text-white !font-['Barlow']" />}
                  style={{ color: '#ffffff' }}
                  size="large"
                >
                  <span className="font-android !font-normal !text-white">CHOOSE AVATAR</span>
                </PrimaryButton>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} justify="end">
          <Col>
            <PrimaryButton
              onClick={onCancel}
              className="mr-2 !text-white !font-['Barlow']"
            >
              CANCEL
            </PrimaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              loading={loading}
              className="!text-black !font-['Barlow']"
            >
              {initialValues ? 'UPDATE' : 'SAVE'}
            </PrimaryButton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}; 